import React, { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const magazineLibrary = [
  { id: 1, title: "January 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20January%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-january-2026.jpg?" },
  { id: 2, title: "February 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20February%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-february-2026.jpg?" },
  { id: 3, title: "March 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20March%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-march-2026.jpg?" },
  { id: 4, title: "April 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20April%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-april-2026.jpg?" },
  { id: 5, title: "May 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20May%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-may-2026.jpg?" },
  { id: 6, title: "June 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20June%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-june-2026.jpg?" },
  { id: 7, title: "June 2026", type: "magazine", file: "https://ik.imagekit.io/wwg4nuz3a/publications/magazine/June%20Magazine.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/magazine-covers/the-clarion-magazine-cover-june-2026.png?" },
  { id: 8, title: "July 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20July%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-july-2026.png" },
  { id: 9, title: "August 2026", type: "newsletter", file: "https://ik.imagekit.io/wwg4nuz3a/publications/The%20Clarion%20August%20Newsletter.pdf?", cover: "https://ik.imagekit.io/wwg4nuz3a/publications/clarion-covers/the-clarion-cover-august-2026.png" },
];
// might need to transfer this to static files in the long run
// optimize magazine loading

const flipVariants = {
  enter: (direction) => ({ rotateY: direction > 0 ? 45 : -45, filter: 'brightness(0.8)', opacity: 0 }),
  center: { rotateY: 0, filter: 'brightness(1)', opacity: 1 },
  exit: (direction) => ({ rotateY: direction < 0 ? 45 : -45, filter: 'brightness(0.8)', opacity: 0 })
};

const PubV3 = () => {
  const [numPages, setNumPages] = useState(null);
  const [[currentPage, direction], setPageWithDirection] = useState([1, 0]);
  const [showControls, setShowControls] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentFile, setCurrentFile] = useState(magazineLibrary[6].file);
  const [scale, setScale] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isDesktopLarge = windowWidth >= 1440;
  const isMobile = windowWidth < 600;
  const isTablet = windowWidth >= 600 && windowWidth < 1440;
  
  const transformRef = useRef(null);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  const DESKTOP_TABLET_SCALE = 0.8;
  const MOBILE_SCALE = 1.0;
  const getInitialScale = () => (windowWidth < 600 ? MOBILE_SCALE : DESKTOP_TABLET_SCALE);

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => console.error(err.message));
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(()=>{
    document.title = "JCI Lucena - The Clarion"
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (transformRef.current) {
        setTimeout(() => {
          transformRef.current.resetTransform(300);
          transformRef.current.centerView(getInitialScale(), 300);
        }, 150);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const startTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!sidebarOpen) timerRef.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleActivity = () => {
    if (!showControls) setShowControls(true);
    startTimer();
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    startTimer();
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sidebarOpen]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onPageRenderSuccess = () => {
    if (transformRef.current) transformRef.current.centerView(getInitialScale(), 0);
  };

  const isSinglePageMode = !isDesktopLarge || currentPage === 1 || currentPage === numPages;

  const paginate = (newDirection) => {
    if (transformRef.current) {
      isMobile ? transformRef.current.resetTransform(250) : transformRef.current.centerView(getInitialScale(), 250);
    }
    let next;
    if (!isDesktopLarge) {
      next = currentPage + newDirection;
    } else {
      if (newDirection === 1) {
        if (currentPage === 1) next = 2;
        else if (currentPage + 2 >= numPages) next = numPages;
        else next = currentPage + 2;
      } else {
        if (currentPage === numPages) {
          next = numPages % 2 === 0 ? numPages - 2 : numPages - 1; 
          if (next < 2) next = 1; 
        } else if (currentPage <= 3) next = 1;
        else next = currentPage - 2;
      }
    }
    if (next >= 1 && next <= numPages) setPageWithDirection([next, newDirection]);
  };

  const getResponsiveWidth = () => {
    if (isDesktopLarge) return 550;
    return Math.min(windowWidth * 0.85, window.innerHeight * 0.72);
  };

  return (
    <div ref={containerRef} style={containerStyle} onClick={() => { if(!sidebarOpen) setShowControls(p => !p); }}>
      <TransformWrapper
        ref={transformRef}
        initialScale={getInitialScale()}
        minScale={getInitialScale()}
        maxScale={4.0}
        centerOnInit={true}
        centerZoomedOut={true}
        limitToBounds={false}
        panning={{ disabled: scale <= getInitialScale(), velocityDisabled: true }} 
        onTransformed={(ref) => setScale(ref.state.scale)}
        // Disable double-tap on Tablet/Desktop to prevent "offset" bugs
        doubleClick={{ disabled: !isMobile, mode: "reset", animationTime: 250 }}
      >
        {({ zoomIn, zoomOut }) => (
          <>
            <AnimatePresence>
              {sidebarOpen && (
                <>
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={sidebarBackdrop} onClick={() => setSidebarOpen(false)} />
                  <motion.div initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} style={sidebarStyle} onClick={(e) => e.stopPropagation()}>
                    <div style={sidebarHeader}>
                      <h3 style={sidebarTitleText}>Archive</h3>
                      <button onClick={() => setSidebarOpen(false)} style={closeSidebarBtn}>✕</button>
                    </div>
                    <div style={scrollArea}>
                      {['magazine', 'newsletter'].map((type) => {
                        const items = magazineLibrary.filter(m => m.type === type);
                        if (items.length === 0) return null;
                        return (
                          <div key={type}>
                            <div style={archiveSectionHeader}>
                              <span style={archiveSectionLabel}>{type === 'magazine' ? 'Magazine' : 'Newsletter'}</span>
                              <div style={archiveSectionDivider} />
                            </div>
                            {items.map((mag) => (
                              <div key={mag.id} style={{ ...thumbContainer, outline: currentFile === mag.file ? '2px solid #fff' : 'none' }}
                                onClick={() => { setCurrentFile(mag.file); setPageWithDirection([1, 0]); setSidebarOpen(false); }}>
                                <img src={mag.cover} alt={mag.title} style={thumbImage} />
                                <p style={thumbTitle}>{mag.title}</p>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showControls && (
                <motion.div 
                  initial={{ y: 100, x: '-50%', opacity: 0 }} animate={{ y: 0, x: '-50%', opacity: 1 }} exit={{ y: 100, x: '-50%', opacity: 0 }} 
                  style={{...hudStyle, bottom: '25px', padding: isMobile ? '8px 12px' : '10px 20px', borderRadius: '50px'}} 
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '24px' }}>
                    {/* <button onClick={() => setSidebarOpen(!sidebarOpen)} style={burgerBtn}><div style={burgerLine} /><div style={burgerLine} /><div style={burgerLine} /></button> */}
                    <button onClick={() => setSidebarOpen(!sidebarOpen)} style={burgerBtn}>Archive</button>
                    <div style={navGroup}>
                      <button onClick={() => paginate(-1)} style={iconBtnStyle}><ArrowLeft /></button>
                      <span style={pageIndicator}>{isSinglePageMode ? currentPage : `${currentPage}-${currentPage + 1}`} / {numPages}</span>
                      <button onClick={() => paginate(1)} style={iconBtnStyle}><ArrowRight /></button>
                    </div>
                    <div style={zoomGroup}>
                        {!isMobile && <><button onClick={() => zoomOut(0.2)} style={zoomBtn}>−</button><button onClick={() => zoomIn(0.2)} style={zoomBtn}>+</button></>}
                        {/* Only show Fullscreen on Desktop */}
                        {isDesktopLarge && (
                          <button onClick={toggleFullScreen} style={zoomBtn}>
                            {isFullscreen ? <ExitFullIcon /> : <EnterFullIcon />}
                          </button>
                        )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <TransformComponent 
              wrapperStyle={{ width: "100vw", height: "100vh" }} 
              contentStyle={{ width: "100vw", height: "100vh", display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div 
                id="magazine-reader" 
                style={{ 
                    ...centeringSafeWrapper, 
                    perspective: '2000px',
                    paddingTop: (isDesktopLarge && !isFullscreen) ? '120px' : '0px'
                }}
              >
                <Document file={currentFile} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={null}>
                   <AnimatePresence mode="wait" custom={direction}>
                      <motion.div 
                        key={`${currentFile}-${currentPage}`}
                        custom={direction}
                        variants={flipVariants}
                        initial="enter" animate="center" exit="exit"
                        transition={{ duration: 0.5, ease: "easeInOut" }}
                        style={{ ...(isSinglePageMode ? singleContainer : spreadContainer), transformStyle: 'preserve-3d' }}
                      >
                        <div style={pageShadow}>
                            <Page pageNumber={currentPage} width={getResponsiveWidth()} renderTextLayer={false} renderAnnotationLayer={false} onRenderSuccess={onPageRenderSuccess} scale={Math.min((window.devicePixelRatio || 1) * 2, 3)} />
                        </div>
                        {!isSinglePageMode && (currentPage + 1 <= numPages) && (
                          <div style={{...pageShadow, borderLeft: '1px solid rgba(0,0,0,0.1)'}}>
                            <Page pageNumber={currentPage + 1} width={getResponsiveWidth()} renderTextLayer={false} renderAnnotationLayer={false} scale={Math.min((window.devicePixelRatio || 1) * 2, 3)} />
                          </div>
                        )}
                      </motion.div>
                    </AnimatePresence>
                </Document>
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

// --- STYLES & ICONS ---
const containerStyle = { width: '100vw', height: '100vh', backgroundColor: '#ebebeb', overflow: 'hidden', position: 'fixed', top: 0, left: 0 };
const centeringSafeWrapper = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', boxSizing: 'border-box' };
const spreadContainer = { display: 'flex', gap: '0px', padding: '10px', borderRadius: '4px' };
const singleContainer = { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' };
const pageShadow = { boxShadow: '0 20px 60px rgba(0,0,0,0.8)', backgroundColor: '#fff', lineHeight: 0 };
const hudStyle = { position: 'fixed', left: '50%', zIndex: 3000, display: 'flex', alignItems: 'center', background: 'rgba(25, 25, 25, 0.85)', backdropFilter: 'blur(15px)', border: '1px solid rgba(255,255,255,0.15)' };
const navGroup = { display: 'flex', alignItems: 'center', gap: '15px' };
const zoomGroup = { display: 'flex', alignItems: 'center', gap: '10px', borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '20px' };
const iconBtnStyle = { background: 'rgba(255, 255, 255, 0.8)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#000' };
const zoomBtn = { background: 'rgba(255, 255, 255, 0.15)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const pageIndicator = { color: 'white', fontSize: '14px', fontWeight: '600', minWidth: '65px', textAlign: 'center' };
const sidebarStyle = { position: 'fixed', left: 0, top: 0, width: '280px', height: '100vh', background: '#111', zIndex: 4000, borderRight: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column' };
const sidebarHeader = { padding: '100px 20px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 };
const scrollArea = { overflowY: 'auto', flexGrow: 1, padding: '0 15px 40px' };
const sidebarTitleText = { margin: 0, color: 'white', fontSize: '1.4rem', fontWeight: 'bold' };
const closeSidebarBtn = { background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' };
const sidebarBackdrop = { position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.7)', zIndex: 3999 };
const thumbContainer = { marginBottom: '20px', marginTop: '20px', cursor: 'pointer', background: '#1a1a1a', padding: '10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' };
const thumbImage = { width: '100%', height: 'auto', borderRadius: '4px' };
const thumbTitle = { color: 'white', fontSize: '0.9rem', marginTop: '10px', textAlign: 'center' };
const archiveSectionHeader = { display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0 8px' };
const archiveSectionLabel = { color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const archiveSectionDivider = { flexGrow: 1, height: '1px', background: 'rgba(255,255,255,0.15)' };
const burgerBtn = { display: 'flex', flexDirection: 'column', gap: '4px', background: 'rgba(255,255,255,0.8)', border: 'none', padding: '10px', borderRadius: '50%', cursor: 'pointer', fontSize: '10px' };
// const burgerBtn = { display: 'flex', flexDirection: 'column', gap: '4px', background: 'rgba(255,255,255,0.15)', border: 'none', padding: '10px', borderRadius: '50%', cursor: 'pointer' };
// const burgerLine = { width: '16px', height: '2px', background: 'white' };

const ArrowLeft = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>;
const ArrowRight = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>;
const EnterFullIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>;
const ExitFullIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M4 12v-8h8M20 12v8h-8M4 4l8 8M20 20l-8-8"/></svg>;

export default PubV3;

// dapat sa homepage pa lang loaded na yung magazine
// medyo mabagal sya on mobile